import { createPortal } from 'react-dom';
import { X, ShieldCheck } from 'lucide-react';
import { useEffect, useState, useRef } from 'react';

interface PaymentMethodModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStripeClick: () => void;
  onPayPalClick: (e?: React.MouseEvent) => void;
  totalAmount: number;
  isProcessing?: boolean;
  errorMessage?: string;
}


export default function PaymentMethodModal({
  isOpen,
  onClose,
  onStripeClick,
  onPayPalClick,
  totalAmount,
  isProcessing = false,
  errorMessage = '',
}: PaymentMethodModalProps) {
  const [mounted, setMounted] = useState(false);
  const prevOverflowRef = useRef('');

  useEffect(() => {
    if (isOpen) {
      prevOverflowRef.current = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      requestAnimationFrame(() => setMounted(true));
    } else {
      setMounted(false);
      document.documentElement.style.overflow = prevOverflowRef.current;
    }

    return () => {
      document.documentElement.style.overflow = prevOverflowRef.current;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isProcessing) onClose();
    };

    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, isProcessing, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center sm:p-4">
      <div
        className={`absolute inset-0 transition-opacity duration-300 ease-out ${
          mounted ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(10px)' }}
        onClick={() => {
          if (!isProcessing) onClose();
        }}
      />

      <div
        className="relative w-full sm:max-w-[420px]"
        style={{
          transition: mounted
            ? 'opacity 0.35s cubic-bezier(0.34,1.56,0.64,1), transform 0.35s cubic-bezier(0.34,1.56,0.64,1)'
            : 'none',
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateY(0) scale(1)' : 'translateY(24px) scale(0.95)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            background: '#ffffff',
            boxShadow: '0 -4px 40px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)',
            overflow: 'hidden',
          }}
          className="rounded-t-[28px] sm:rounded-[28px]"
        >
          <div
            className="sm:hidden mx-auto mt-3 mb-0 w-9 h-1 rounded-full"
            style={{ background: 'rgba(0,0,0,0.1)' }}
          />

          <div
            className="relative px-6 pt-5 pb-6"
            style={{ background: 'linear-gradient(150deg, #c8102e 0%, #9b0c23 100%)' }}
          >
            <button
              type="button"
              onClick={() => { if (!isProcessing) onClose(); }}
              disabled={isProcessing}
              className="absolute top-4 right-4 flex items-center justify-center transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ width: 30, height: 30, borderRadius: '50%', background: 'rgba(255,255,255,0.15)' }}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.25)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.15)')}
            >
              <X className="w-3.5 h-3.5 text-white" />
            </button>

            <p className="text-[10px] font-semibold tracking-[0.18em] uppercase mb-2" style={{ color: 'rgba(255,255,255,0.5)' }}>
              Checkout
            </p>
            <h2 className="text-xl font-bold text-white leading-tight mb-4">
              Zahlungsmethode wählen
            </h2>

            <div
              className="inline-flex items-baseline gap-2 px-4 py-2.5 rounded-2xl"
              style={{ background: 'rgba(0,0,0,0.18)' }}
            >
              <span className="text-[11px] font-medium" style={{ color: 'rgba(255,255,255,0.6)' }}>
                Zu zahlen
              </span>
              <span className="text-[26px] font-extrabold text-white leading-none tracking-tight">
                {totalAmount.toFixed(2)}&thinsp;&euro;
              </span>
            </div>
          </div>

          <div className="px-5 pt-5 pb-4 space-y-3" style={{ background: '#ffffff' }}>
            {totalAmount > 0 && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onStripeClick();
                }}
                disabled={isProcessing}
                className={`w-full py-4 px-6 rounded-[14px] font-semibold text-white transition-all duration-300 text-[13px] tracking-wide ${
                  isProcessing
                    ? 'bg-gray-200 cursor-not-allowed text-gray-400'
                    : 'bg-gray-900 hover:bg-gray-800 shadow-sm hover:shadow-md'
                }`}
              >
                {isProcessing ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Wird verarbeitet...</span>
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                      <line x1="1" y1="10" x2="23" y2="10" />
                    </svg>
                    <span>Mit Karte / Apple Pay bezahlen</span>
                  </span>
                )}
              </button>
            )}

            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onPayPalClick(e);
              }}
              disabled={isProcessing}
              className={`w-full py-4 px-6 rounded-[14px] font-semibold text-white transition-all duration-300 text-[13px] tracking-wide ${
                isProcessing
                  ? 'bg-gray-200 cursor-not-allowed text-gray-400'
                  : totalAmount > 0
                    ? 'bg-[#0070BA] hover:bg-[#005EA6] shadow-sm'
                    : 'bg-gray-900 hover:bg-gray-800 shadow-sm hover:shadow-md'
              }`}
            >
              {isProcessing ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  <span>Weiterleitung zu PayPal...</span>
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  {totalAmount > 0 ? (
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.905 9.5c.21-1.337.11-2.246-.36-3.13C19.698 5.076 18.171 4.5 15.875 4.5H8.042c-.51 0-.943.37-1.022.87L4.5 19.5h4.375l1.096-6.875h2.904c4.751 0 7.596-2.283 8.03-6.125zM9.931 9.5l.594-3.75h3.35c1.288 0 2.003.197 2.375.625.298.344.344.862.188 1.625-.297 1.875-1.594 2.5-3.906 2.5H9.931z"/>
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                  <span>{totalAmount > 0 ? 'Mit PayPal bezahlen' : 'Kostenlos buchen'}</span>
                </span>
              )}
            </button>

            {errorMessage && (
              <div
                className="rounded-xl p-3.5 flex items-start gap-3"
                style={{ background: '#fff5f6', border: '1.5px solid #fecdd3' }}
              >
                <svg className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <div className="text-[13px] font-semibold text-red-800 mb-0.5">Fehler bei der Zahlung</div>
                  <div className="text-[12px] text-red-600">{errorMessage}</div>
                </div>
              </div>
            )}
          </div>

          <div className="px-5 pb-5" style={{ background: '#ffffff' }}>
            <div className="flex items-center justify-center gap-2 pt-1 pb-1">
              <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#16a34a', opacity: 0.7 }} />
              <span className="text-[11px]" style={{ color: '#9ca3af' }}>
                SSL-verschlüsselt&nbsp;·&nbsp;Sofortige Bestätigung
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
